import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import bannerImage from "@/assets/mi-cta-banner.jpg";

interface MedicalInfoCTABannerProps {
  title: string;
  description: string;
  ctaLabel: string;
  href: string;
}

/**
 * Full-width CTA banner matching the ContactCTA style: dark blue overlay
 * over a subtle background image, white headline/subtext, white rounded
 * button with arrow. Used after the Chapters section on on-demand pages.
 */
const MedicalInfoCTABanner = ({ title, description, ctaLabel, href }: MedicalInfoCTABannerProps) => {
  const lang = useLanguage();

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden">
      <img
        src={bannerImage}
        alt=""
        width={1920}
        height={800}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-primary/80" />
      <div className="absolute top-1/4 -left-20 w-72 h-72 rounded-full bg-accent/15 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-16 w-56 h-56 rounded-full bg-accent/10 blur-[80px] pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground tracking-tight leading-tight mb-5">
            {title}
          </h2>
          <p className="text-primary-foreground/70 text-base mb-10">
            {description}
          </p>
          <div className="flex justify-center">
            <a
              href={localizeHref(href, lang)}
              className="group inline-flex items-center gap-2 bg-primary-foreground text-primary px-7 py-3.5 rounded-full text-sm font-semibold min-h-[48px] hover:bg-primary-foreground/90 hover:shadow-xl hover:scale-[1.03] transition-all duration-300"
            >
              {ctaLabel} <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MedicalInfoCTABanner;
